/**
 * The Build gadgets' plain logic (components/home/gadgets: Preview, Code activity, Dependencies, Agents at work, Open
 * work). Pure: the answers of vfs-service go in, what the gadget shows comes out. Nothing is invented: a figure the
 * answer does not carry is null, and the gadget says so.
 */
import { defaultEntry } from '../shareEntry';
import { shortServiceName } from '../gitRepoSource';
import { agentName, agentStepLabel } from '../presenceModel';

const baseName = (path) => String(path || '').split('/').pop();

/**
 * The service the Preview gadget shows: the project's default app (`preferred`), else the share link's default (`app`,
 * else the first by name). null without services.
 */
export function mainServiceItem(items = [], preferred = null) {
  const names = items.map((item) => shortServiceName(item.serviceName)).filter(Boolean).sort((a, b) => a.localeCompare(b));
  const entry = defaultEntry(names, preferred);
  return items.find((item) => shortServiceName(item.serviceName) === entry) || null;
}

/** The service a vfs path belongs to (`services/api/src/x.js` → `api`), or null for the project's own files. */
export const serviceOfPath = (path) => {
  const match = /^services\/([^/]+)\//.exec(String(path || ''));
  return match ? match[1] : null;
};

/**
 * Per service: commits and changed files in the changes (`GET /collab/changes?since=`, newest first), the branch and
 * what is not committed (`GET /git/overview` repos: `changed`, `untracked`). `[{ service, commits, files, branch,
 * uncommitted }]`, busiest first; `uncommitted`/`branch` null when git did not say. The project's own files (brain, the
 * root) are one row, `service: null`, when they changed.
 */
export function codeActivity(changes = [], repos = []) {
  const rows = new Map();
  const row = (service) => {
    const id = service || '';
    if (!rows.has(id)) { rows.set(id, { service, commits: new Set(), files: new Set(), branch: null, uncommitted: null }); }
    return rows.get(id);
  };
  (repos || []).forEach((repo) => {
    const entry = row(shortServiceName(repo.service));
    entry.branch = repo.branch || null;
    const counted = [repo.changed, repo.untracked].filter((n) => Number.isFinite(n));
    entry.uncommitted = counted.length ? counted.reduce((a, b) => a + b, 0) : null;
  });
  (changes || []).forEach((change) => {
    (change.files || []).forEach((file) => {
      const entry = row(serviceOfPath(file.path));
      if (change.sha) { entry.commits.add(change.sha); }
      entry.files.add(file.path);
    });
  });
  return [...rows.values()]
    .map((entry) => ({ ...entry, commits: entry.commits.size, files: entry.files.size }))
    .filter((entry) => entry.service || entry.commits > 0)
    .sort((a, b) => b.commits - a.commits || b.files - a.files || String(a.service).localeCompare(String(b.service)));
}

/**
 * From the Quality overview: the dependencies check's outdated packages per service (null when the registry was not
 * asked: `outdatedNote` says why) and the vulnerable packages the vulnerabilities check lists first (`top`), with the
 * check's own counts. `ran`: false before either check ran once.
 */
export function dependencySummary(overview) {
  const deps = overview?.checks?.dependencies || null;
  const vulns = overview?.checks?.vulnerabilities || null;
  const ranDeps = Boolean(deps && deps.ranAt && deps.status !== 'skipped');
  const ranVulns = Boolean(vulns && vulns.ranAt && vulns.status !== 'skipped');
  const outdated = ranDeps && Array.isArray(deps.details?.outdated) ? deps.details.outdated : null;
  const perService = new Map();
  const add = (service, key, item) => {
    const name = shortServiceName(service || '') || 'project';
    if (!perService.has(name)) { perService.set(name, { service: name, outdated: [], vulnerable: [] }); }
    perService.get(name)[key].push(item);
  };
  (outdated || []).forEach((o) => add(o.service, 'outdated', { package: o.package, current: o.current, latest: o.latest, major: Boolean(o.major) }));
  (ranVulns ? vulns.top || [] : []).forEach((f) => {
    const services = f.extra?.services?.length ? f.extra.services : [f.service];
    services.forEach((s) => add(s, 'vulnerable', { package: f.extra?.package || f.rule, version: f.extra?.version || null, fixed: f.extra?.fixed || null, severity: f.severity }));
  });
  const counts = vulns?.counts || {};
  const vulnerableTotal = ranVulns ? ['critical', 'high', 'medium', 'low'].reduce((sum, k) => sum + (Number(counts[k]) || 0), 0) : null;
  return {
    ran: ranDeps || ranVulns,
    outdatedKnown: outdated !== null,
    outdatedNote: ranDeps && outdated === null ? (deps.details?.outdatedNote || 'Outdated packages are not looked up') : null,
    outdatedTotal: outdated ? outdated.length : null,
    vulnerableTotal,
    services: [...perService.values()].sort((a, b) => (b.vulnerable.length + b.outdated.length) - (a.vulnerable.length + a.outdated.length)),
  };
}

/**
 * The agents in presence now: `[{ id, name, file, step, idle, paused, model }]`; `idle`: not writing any file now.
 * Those writing first.
 */
export function agentRows(presence) {
  const you = presence?.you || null;
  return (presence?.agents || [])
    .filter((agent) => agent && agent.id)
    .map((agent) => ({
      id: agent.id,
      name: agentName(agent, you),
      file: (agent.paths || []).length ? baseName(agent.paths[0]) : null,
      files: (agent.paths || []).length,
      step: agentStepLabel(agent),
      idle: !(agent.paths || []).length,
      paused: Boolean(agent.paused),
    }))
    .sort((a, b) => Number(a.idle) - Number(b.idle) || a.name.localeCompare(b.name));
}

/** The secrets a service declares but nobody set yet, from the app configuration overview: `[{ service, name }]`. */
export const secretsToSet = (appConfig) => (appConfig?.services || [])
  .flatMap((service) => (service.secretsMissing || []).map((name) => ({ service: service.name, name })));

/** The release gate's failing rules: `[{ id, label, detail }]`. */
export const failingRules = (gate) => (Array.isArray(gate?.rules) ? gate.rules : [])
  .filter((rule) => rule.status === 'fail')
  .map((rule) => ({ id: rule.id, label: rule.label, detail: rule.detail || null }));

/** TODO / FIXME found by the code search (`[{ path, line, match }]`), capped at `max`: `{ count, capped, files }`. */
export function todoCount(matches, max) {
  const list = Array.isArray(matches) ? matches : [];
  return { count: list.length, capped: Boolean(max) && list.length >= max, files: new Set(list.map((m) => m.path)).size };
}
